<template>
  <el-dialog
    :title="isEdit ? $t('subAccount.role.edit') : $t('subAccount.role.create')"
    :visible="visible"
    :close-on-click-modal="false"
    width="760px"
    @close="handleClose"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      :label-width="$i18n.locale == 'zh' ? '100px' : '150px'"
      label-position="left"
    >
      <el-form-item :label="$t('subAccount.role.roleName')" prop="name">
        <el-input
          v-model.trim="form.name"
          style="width: 320px"
          :placeholder="$t('subAccount.role.nameFieldPh')"
        />
      </el-form-item>

      <el-form-item :label="$t('subAccount.role.appPerm')" prop="isAllApps" required>
        <el-radio-group :value="form.isAllApps" @input="onScopeChange">
          <el-radio :label="true">{{ $t('subAccount.role.allApps') }}</el-radio>
          <el-radio :label="false">{{ $t('subAccount.role.someApps') }}</el-radio>
        </el-radio-group>
        <!-- 选「全部应用」时选择器整块隐藏，但已勾的应用留在 form 里 ——
             同一次弹窗里来回切两个选项不该把用户勾好的清掉-->
        <app-permission-picker
          v-if="form.isAllApps === false"
          v-model="form.appIds"
          :apps="apps"
          :loading="appsLoading"
        />
      </el-form-item>

      <el-form-item :label="$t('subAccount.role.menuPerm')" prop="menuKeys" required>
        <menu-permission-picker v-model="form.menuKeys" :tree="menuTree" />
      </el-form-item>
    </el-form>

    <span slot="footer">
      <el-button @click="handleClose">{{ $t('cancel') }}</el-button>
      <el-button type="primary" :loading="saving" @click="submit">{{ $t('confirm') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import AppPermissionPicker from './AppPermissionPicker'
import MenuPermissionPicker from './MenuPermissionPicker'

const NAME_MAX = 20

export default {
  name: 'RoleDialog',
  components: { AppPermissionPicker, MenuPermissionPicker },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    // 编辑时传入角色行数据，新建传 null
    role: {
      type: Object,
      default: null
    },
    // 应用候选项由列表页拉一次后传进来
    apps: {
      type: Array,
      default: () => []
    },
    appsLoading: {
      type: Boolean,
      default: false
    },
    // 可授权菜单树，由登录下发的菜单树滤出来，同样由列表页算好传进来
    menuTree: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      saving: false,
      form: {
        id: null,
        name: '',
        // null = 还没选，对应 PRD 的「应用权限必选」校验
        isAllApps: null,
        appIds: [],
        menuKeys: []
      }
    }
  },
  computed: {
    isEdit() {
      return !!(this.role && this.role.id)
    },
    // 放 computed 里，切换语言时提示文案跟着变
    rules() {
      const t = key => this.$t('subAccount.role.' + key)
      return {
        name: [
          { required: true, message: t('errNameEmpty'), trigger: 'blur' },
          { max: NAME_MAX, message: t('errNameLength'), trigger: 'blur' }
        ],
        // 应用权限两种没填：一个都没选，或者选了「指定应用」却没勾应用
        isAllApps: [{
          validator: (rule, val, cb) => {
            if (val === null) return cb(new Error(t('selectAppPerm')))
            if (val === false && !this.form.appIds.length) return cb(new Error(t('errAppEmpty')))
            cb()
          }
        }],
        menuKeys: [{
          validator: (rule, val, cb) => (val.length ? cb() : cb(new Error(t('errMenuEmpty'))))
        }]
      }
    }
  },
  watch: {
    visible(val) {
      if (val) this.reset()
    },
    // 两个权限选择器是自定义组件，不会像 el-input 那样自己通知表单重新校验。
    // 已经标红的字段，用户一改就再校验一次，改对了红字立刻消失
    'form.isAllApps'() {
      this.recheck('isAllApps')
    },
    'form.appIds'() {
      this.recheck('isAllApps')
    },
    'form.menuKeys'() {
      this.recheck('menuKeys')
    }
  },
  created() {
    if (this.visible) this.reset()
  },
  methods: {
    reset() {
      const r = this.role
      this.saving = false
      this.form = {
        id: r && r.id ? r.id : null,
        name: r ? r.name || '' : '',
        isAllApps: r ? !!r.is_all_apps : null,
        // 上次存的是「指定应用」才回填勾选；存的是「全部应用」时这次切到「指定应用」
        // 从空开始 —— 上次保存时那些勾选并没有生效，回填会让人误以为它们一直在
        appIds: r && !r.is_all_apps ? (r.app_ids || []).slice() : [],
        menuKeys: r ? (r.menus || []).map(m => m.menu_key || m) : []
      }
      // 清掉上次打开残留的红字
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    onScopeChange(val) {
      this.form.isAllApps = val
    },
    recheck(prop) {
      const fields = (this.$refs.form && this.$refs.form.fields) || []
      const item = fields.filter(f => f.prop === prop)[0]
      if (item && item.validateState === 'error') item.validate('')
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (valid) this.save()
      })
    },
    save() {
      const f = this.form
      const params = {
        name: f.name,
        menu_keys: f.menuKeys.slice(),
        is_all_apps: f.isAllApps,
        // 「全部应用」不带应用清单，后端也不该存 —— 存了以后加的新应用会被漏掉
        app_ids: f.isAllApps ? [] : f.appIds.slice()
      }
      if (f.id) params.id = f.id
      this.saving = true
      this.__postData('roleEdit', params, res => {
        this.saving = false
        if (res && res.code == 1) {
          this.$message.error(res.msg || this.$t('subAccount.role.errNameExists'))
          return
        }
        this.$message.success(
          this.$t(this.isEdit ? 'subAccount.role.saved' : 'subAccount.role.created')
        )
        this.$emit('saved')
        this.handleClose()
      }, errmsg => {
        this.saving = false
        this.$message.error(errmsg)
      }).catch(() => {
        this.saving = false
      })
    },
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    }
  }
}
</script>
